import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, User, Heart, MessageCircle, Send, LogOut } from 'lucide-react';
import { io } from 'socket.io-client';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import NotificationBell from './NotificationBell';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [requestsBadgeCount, setRequestsBadgeCount] = useState(0);
  const [messageToast, setMessageToast] = useState(null);
  const [proposalBadgeCount, setProposalBadgeCount] = useState(0);
  const socketRef = useRef(null);
  const toastTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const profileRedirect = `${location.pathname}${location.search}`;
  const isProviderProfilePage = /^\/provider\/[^/]+$/.test(location.pathname);
  const loginPath = isProviderProfilePage ? `/login?redirect=${encodeURIComponent(profileRedirect)}` : '/login';
  const registerPath = isProviderProfilePage ? `/register?redirect=${encodeURIComponent(profileRedirect)}` : '/register';

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (!user) {
      setUnreadMessages(0);
      setRequestsBadgeCount(0);
      return;
    }

    fetchUnreadMessages();

    if (user.role === 'provider') {
      fetchRequestsBadgeCount();
    } else {
      setRequestsBadgeCount(0);
    }

    const interval = setInterval(() => {
      fetchUnreadMessages();
      if (user.role === 'provider') {
        fetchRequestsBadgeCount();
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

    socketRef.current = io(backendUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      withCredentials: true
    });

    socketRef.current.on('connect', () => {
      socketRef.current.emit('authenticate', { user_id: user.user_id });
    });

    socketRef.current.on('new_message', (data) => {
      console.log('NAVBAR NEW MESSAGE TOAST:', data);
      setUnreadMessages(prev => prev + 1);

      setMessageToast({
        conversation_id: data.conversation_id,
        message: data.message
      });

      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }

      toastTimeoutRef.current = setTimeout(() => {
        setMessageToast(null);
      }, 6000);
    });

    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }

      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [user]);

  const checkAuth = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadMessages = async () => {
    try {
      const response = await api.get('/chat/unread-count');
      const nextUnread = response.data.unread || 0;

      if (nextUnread > unreadMessages && unreadMessages > 0) {
        setMessageToast({
          conversation_id: null,
          message: 'Tienes un mensaje sin leer.'
        });

        if (toastTimeoutRef.current) {
          clearTimeout(toastTimeoutRef.current);
        }

        toastTimeoutRef.current = setTimeout(() => {
          setMessageToast(null);
        }, 6000);
      }

      setUnreadMessages(nextUnread);
    } catch (error) {
      setUnreadMessages(0);
    }
  };

  const fetchRequestsBadgeCount = async () => {
    try {
      const [directRes, publishedRes] = await Promise.all([
        api.get('/contact-requests/received').catch(() => ({ data: [] })),
        api.get('/care-requests').catch(() => ({ data: [] }))
      ]);

      const pendingDirect = (directRes.data || []).filter(r => r.status === 'pending').length;
      const publishedCount = (publishedRes.data || []).length;

      setRequestsBadgeCount(pendingDirect + publishedCount);
    } catch (error) {
      setRequestsBadgeCount(0);
    }
  };

  const goToMessageToast = () => {
    setMessageToast(null);
    navigate('/chat');
  };

  useEffect(() => {
    const fetchProposalBadge = async () => {
      if (!user || user.role === 'provider' || user.role === 'admin') {
        setProposalBadgeCount(0);
        return;
      }

      try {
        const res = await api.get('/proposals/received');
        setProposalBadgeCount((res.data || []).filter(p => p.status === 'pending').length);
      } catch {
        setProposalBadgeCount(0);
      }
    };

    fetchProposalBadge();
  }, [user]);

  useEffect(() => {
    const fetchFavoritesCount = async () => {
      if (!user || user.role === 'provider' || user.role === 'admin') {
        setFavoritesCount(0);
        return;
      }

      try {
        const res = await api.get('/favorites');
        const favoritesData = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.favorites)
            ? res.data.favorites
            : [];

        setFavoritesCount(favoritesData.length);
      } catch (error) {
        setFavoritesCount(0);
      }
    };

    fetchFavoritesCount();

    const refreshFavorites = () => fetchFavoritesCount();
    window.addEventListener('ucan:favorites-updated', refreshFavorites);
    window.addEventListener('focus', refreshFavorites);

    return () => {
      window.removeEventListener('ucan:favorites-updated', refreshFavorites);
      window.removeEventListener('focus', refreshFavorites);
    };
  }, [user]);

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      localStorage.removeItem('jwt_token');
      setUser(null);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('jwt_token');
      setUser(null);
      navigate('/');
    }
  };

  return (
    <>
    <nav className="bg-white shadow-sm sticky top-0 z-50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-28">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src="/ucan-logo-rojo.svg" alt="U-CAN" className="h-[100px]" data-testid="navbar-logo" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to={user?.role === 'provider' ? '/provider/requests' : '/search'}
              className="relative text-gray-700 hover:text-[#E6202E] font-medium transition-colors"
            >
              {user?.role === 'provider' ? 'Solicitudes' : 'Buscar Servicios'}
              {user?.role === 'provider' && requestsBadgeCount > 0 && (
                <span className="absolute -top-3 -right-5 bg-[#E6202E] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none">
                  {requestsBadgeCount > 99 ? '99+' : requestsBadgeCount}
                </span>
              )}
            </Link>
            {user && user.role !== 'provider' && user.role !== 'admin' && (
              <Link
                to="/client/proposals"
                className="relative text-gray-700 hover:text-[#E6202E] font-medium transition-colors flex items-center gap-1"
              >
                <Send className="w-4 h-4" />
                Propuestas
                {proposalBadgeCount > 0 && (
                  <span className="absolute -top-3 -right-5 bg-[#E6202E] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none">
                    {proposalBadgeCount > 99 ? '99+' : proposalBadgeCount}
                  </span>
                )}
              </Link>
            )}
            
            {user ? (
              <>
                <Link to="/favoritos" className="text-gray-700 hover:text-[#E6202E]">
                  <Heart className={`w-5 h-5 ${favoritesCount > 0 ? 'text-[#E6202E] fill-[#E6202E]' : ''}`} />
                </Link>
                <Link to="/chat" className="relative text-gray-700 hover:text-[#E6202E]">
                  <MessageCircle className="w-5 h-5" />
                  {unreadMessages > 0 && (
                    <span className="absolute -top-2 -right-3 bg-[#E6202E] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none">
                      {unreadMessages > 99 ? '99+' : unreadMessages}
                    </span>
                  )}
                </Link>
                <div className="relative group">
                  <button className="flex items-center space-x-2 text-gray-700 hover:text-[#E6202E] py-2">
                  {user.picture ? (
                      <img src={user.picture.startsWith('http') ? user.picture : `${process.env.REACT_APP_BACKEND_URL}${user.picture.startsWith('/uploads/') ? '/api' + user.picture : user.picture}`} alt={user.name} className="w-8 h-8 rounded-full" />
                    ) : (
                      <User className="w-5 h-5" />
                    )}
                    <span className="font-medium">{user.name}</span>
                  </button>
                  <div className="absolute right-0 top-full pt-2 w-48 invisible group-hover:visible">
                    <div className="bg-white rounded-lg shadow-lg py-2 border">
                    {user.role === 'provider' ? (
                      <Link to="/provider/account" className="block px-4 py-2 text-gray-700 hover:bg-gray-50">
                        Mi Cuenta
                      </Link>
                    ) : (
                      <Link to="/account" className="block px-4 py-2 text-gray-700 hover:bg-gray-50">
                        Mi Cuenta
                      </Link>
                    )}
                    {user.role === 'client' && (
                      <Link to="/dashboard" className="block px-4 py-2 text-gray-700 hover:bg-gray-50">
                        Panel Cliente
                      </Link>
                    )}
                    <Link to="/historial" className="block px-4 py-2 text-gray-700 hover:bg-gray-50">
                      Mis servicios
                    </Link>
                    {user.role === 'provider' && (
                      <Link to="/provider/dashboard" className="block px-4 py-2 text-gray-700 hover:bg-gray-50">
                        Panel Cuidador
                      </Link>
                    )}
                    {user.role === 'admin' && (
                      <Link to="/admin" className="block px-4 py-2 text-gray-700 hover:bg-gray-50">
                        Administración
                      </Link>
                    )}
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Cerrar Sesión</span>
                    </button>
                    </div>
                  </div>
                </div>
                <NotificationBell />
              </>
            ) : (
              !loading && (
                <>
                  <Link to={loginPath}>
                    <Button variant="outline" className="border-[#E6202E] text-[#E6202E] hover:bg-red-50">
                      Iniciar Sesión
                    </Button>
                  </Link>
                  <Link to={registerPath}>
                    <Button className="bg-[#E6202E] hover:bg-[#D31522] text-white">
                      Registrarse
                    </Button>
                  </Link>
                </>
              )
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-gray-700 hover:text-[#E6202E]"
            data-testid="mobile-menu-button"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
     {/* Mobile Navigation */}
     {isOpen && (
          <div className="md:hidden py-4 border-t">
            <Link
              to={user?.role === 'provider' ? '/provider/requests' : '/search'}
              className="block py-2 text-gray-700 hover:text-[#E6202E]"
              onClick={() => setIsOpen(false)}
            >
              {user?.role === 'provider' ? 'Solicitudes' : 'Buscar Servicios'}
            </Link>
            {user && user.role !== 'provider' && user.role !== 'admin' && (
              <Link to="/client/proposals" className="block py-2 text-gray-700 hover:text-[#E6202E] flex items-center gap-2" onClick={() => setIsOpen(false)}>
                <span className="relative inline-flex">
                  <Send className="w-4 h-4" />
                  {proposalBadgeCount > 0 && (
                    <span className="absolute -top-2 -right-3 bg-[#E6202E] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none">
                      {proposalBadgeCount > 99 ? '99+' : proposalBadgeCount}
                    </span>
                  )}
                </span>
                Propuestas
              </Link>
            )}
            {user ? (
              <>
                {user.role === 'provider' ? (
                  <Link to="/provider/account" className="block py-2 text-gray-700 hover:text-[#E6202E]" onClick={() => setIsOpen(false)}>
                    Mi Cuenta
                  </Link>
                ) : (
                  <Link to="/account" className="block py-2 text-gray-700 hover:text-[#E6202E]" onClick={() => setIsOpen(false)}>
                    Mi Cuenta
                  </Link>
                )}
                {user.role === 'client' && (
                  <Link to="/dashboard" className="block py-2 text-gray-700 hover:text-[#E6202E]" onClick={() => setIsOpen(false)}>
                    Panel Cliente
                  </Link>
                )}
                <Link to="/historial" className="block py-2 text-gray-700 hover:text-[#E6202E]" onClick={() => setIsOpen(false)}>
                  Mis servicios
                </Link>
                {user.role === 'provider' && (
                  <Link to="/provider/dashboard" className="block py-2 text-gray-700 hover:text-[#E6202E]" onClick={() => setIsOpen(false)}>
                    Panel Cuidador
                  </Link>
                )}
                {user.role === 'admin' && (
                  <Link to="/admin" className="block py-2 text-gray-700 hover:text-[#E6202E] font-semibold" onClick={() => setIsOpen(false)} data-testid="mobile-admin-link">
                    Administración
                  </Link>
                )}
                <Link to="/favoritos" className="block py-2 text-gray-700 hover:text-[#E6202E] flex items-center gap-2" onClick={() => setIsOpen(false)}>
                  <Heart className="w-4 h-4" /> Favoritos
                </Link>
                <Link to="/chat" className="block py-2 text-gray-700 hover:text-[#E6202E] flex items-center gap-2" onClick={() => setIsOpen(false)}>
                  <span className="relative inline-flex">
                    <MessageCircle className="w-4 h-4" />
                    {unreadMessages > 0 && (
                      <span className="absolute -top-2 -right-3 bg-[#E6202E] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none">
                        {unreadMessages > 99 ? '99+' : unreadMessages}
                      </span>
                    )}
                  </span>
                  Chat
                </Link>
                <div className="py-2 flex items-center gap-2" onClick={() => setIsOpen(false)}>
                  <NotificationBell />
                  <span className="text-gray-700">Notificaciones</span>
                </div>
                <button
                  onClick={() => { setIsOpen(false); handleLogout(); }}
                  className="block w-full text-left py-2 text-gray-700 hover:text-[#E6202E] flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" /> Cerrar Sesión
                </button>
              </>
            ) : (
              !loading && (
                <>
                  <Link to="/login" className="block w-full text-left py-2 text-gray-700 hover:text-[#E6202E]" onClick={() => setIsOpen(false)}>
                    Iniciar Sesión
                  </Link>
                  <Link to="/register" className="block w-full text-left py-2 text-[#E6202E] font-bold" onClick={() => setIsOpen(false)}>
                    Registrarse
                  </Link>
                </>
              )
            )}
          </div>
        )}  

      </div>
    </nav>

    {messageToast && (
      <div className="fixed top-28 left-1/2 -translate-x-1/2 z-[99999] w-[360px] max-w-[calc(100vw-2rem)] bg-white border border-gray-200 shadow-2xl rounded-2xl p-4">
        <div className="flex gap-3">
          <div className="w-10 h-10 rounded-full bg-[#E6202E] text-white flex items-center justify-center flex-shrink-0">
            <MessageCircle className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-gray-900">¡Tienes un nuevo mensaje!</p>
            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
              {messageToast.message || 'Tienes un mensaje sin leer.'}
            </p>
            <button
              onClick={goToMessageToast}
              className="mt-3 bg-[#E6202E] hover:bg-[#D31522] text-white text-sm font-semibold px-4 py-2 rounded-lg"
            >
              Ver conversación
            </button>
          </div>
          <button
            onClick={() => setMessageToast(null)}
            className="text-gray-400 hover:text-gray-700 h-6"
            aria-label="Cerrar aviso"
          >
            ×
          </button>
        </div>
      </div>
    )}
    </>
  );
};

export default Navbar;
